// angular
import { afterNextRender, Component, computed, effect, inject, input, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged } from 'rxjs';
// components
import { CheckBoxComponent } from '@app/core/components/checkbox/checkbox.component';
import { FiltersBoxComponent } from '@app/core/components/filters-box/filters-box.component';
import { FiltersSectionComponent } from '@app/core/components/filters-section/filters-section.component';
// services
import { CategoriesService } from '@app/+categories/shared/services/categories.service';
// types
import { CategoriesType } from '@app/+categories/shared/types/categories.type';
import { II18n } from '@app/shared/interfaces/i18n.interface';
import { I18n } from '@shared/enums/i18n.enum';

function mapCategoriesForRadio(
  categories: CategoriesType[]
): { _id: string; name: II18n }[] {
  return categories.map(c => ({ _id: c._id, name: c.title }));
}

@Component({
  selector: 'pwj-solutions-people-filters',
  standalone: true,
  imports: [
    CheckBoxComponent,
    FiltersBoxComponent,
    FiltersSectionComponent,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
  ],
  templateUrl: './solutions-people-filters.component.html',
  styleUrl: './solutions-people-filters.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class SolutionsPeopleFiltersComponent {
  layoutVariant = input<'default' | 'dialog'>('default');
  filtersBoxPanelVariant = computed(() =>
    this.layoutVariant() === 'dialog' ? 'standalone' : 'embedded'
  );

  categoryItems: { _id: string; name: II18n }[] = [];
  readonly categoryFilterQueryParams: Params = { page: null };

  readonly experienceMin = 0;
  readonly experienceMax = 50;
  readonly experienceStep = 1;

  minExperience = this.experienceMin;
  maxExperience = this.experienceMax;

  readonly filtersForm = inject(FormBuilder).nonNullable.group({
    trade: '',
  });

  bimReady = false;

  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _categoriesService = inject(CategoriesService);
  private readonly _translate = inject(TranslateService);
  private _syncingFromQueryParams = false;

  constructor() {
    effect(() => {
      const data = this._categoriesService.categoriesData();
      this.categoryItems = mapCategoriesForRadio(data ?? []);
    });

    this._activatedRoute.queryParams.subscribe(p => {
      this._syncingFromQueryParams = true;

      this.minExperience =
        p['min_age'] != null && p['min_age'] !== ''
          ? Number(p['min_age'])
          : this.experienceMin;
      this.maxExperience =
        p['max_age'] != null && p['max_age'] !== ''
          ? Number(p['max_age'])
          : this.experienceMax;

      this.filtersForm.patchValue(
        {
          trade: p['trade'] != null ? String(p['trade']) : '',
        },
        { emitEvent: false }
      );
      this.bimReady = p['bim_ready'] === true || p['bim_ready'] === 'true';

      this._syncingFromQueryParams = false;
    });

    this.filtersForm.valueChanges
      .pipe(
        debounceTime(350),
        distinctUntilChanged((a, b) => a.trade === b.trade),
        takeUntilDestroyed()
      )
      .subscribe(() => this.persistTradeFilter());

    afterNextRender(() => {
      if (this._categoriesService.categoriesData().length === 0) {
        this._categoriesService.getCategories();
      }
    });
  }

  get experienceMinPercent(): number {
    return this.getExperiencePercent(this.minExperience);
  }

  get experienceMaxPercent(): number {
    return this.getExperiencePercent(this.maxExperience);
  }

  get experienceFillWidthPercent(): number {
    return this.experienceMaxPercent - this.experienceMinPercent;
  }

  get isExperienceRtl(): boolean {
    return (I18n as Record<string, string>)[this._translate.currentLang]?.includes(
      I18n.fa || I18n.ar
    );
  }

  get experienceRangeAriaLabel(): string {
    const years = this._translate.instant('solutions.filters.experienceYears');
    const from = this._translate.instant('global.from');
    const to = this._translate.instant('global.to');
    return `${from} ${this.minExperience} ${years} ${to} ${this.maxExperience} ${years}`;
  }

  onMinExperienceChange(event: Event): void {
    const value = Math.min(
      Number((event.target as HTMLInputElement).value),
      this.maxExperience - this.experienceStep
    );

    this.minExperience = Math.max(this.experienceMin, value);
    this.persistExperienceFilter();
  }

  onMaxExperienceChange(event: Event): void {
    const value = Math.max(
      Number((event.target as HTMLInputElement).value),
      this.minExperience + this.experienceStep
    );

    this.maxExperience = Math.min(this.experienceMax, value);
    this.persistExperienceFilter();
  }

  private getExperiencePercent(value: number): number {
    return Math.round(
      ((value - this.experienceMin) / (this.experienceMax - this.experienceMin)) * 100
    );
  }

  private persistExperienceFilter(): void {
    if (this._syncingFromQueryParams) return;

    const min =
      this.minExperience > this.experienceMin ? String(this.minExperience) : null;
    const max =
      this.maxExperience < this.experienceMax ? String(this.maxExperience) : null;

    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParams: {
        min_age: min,
        max_age: max,
        page: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  private persistTradeFilter(): void {
    const t = this.filtersForm.getRawValue().trade.trim();
    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParams: {
        trade: t !== '' ? t : null,
        page: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  onBimReadyChange(checked: boolean): void {
    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParams: {
        bim_ready: checked ? true : null,
        page: null,
      },
      queryParamsHandling: 'merge',
    });
  }
}
